// src/lib/dashboard/metasMensais.ts
//
// Metas mês a mês dos indicadores (tabela `metas_mensais`, #6496). Lugar único
// onde as metas são guardadas. Quem grava: a aba Metas do Painel Executivo e, só
// para a meta do Comercial (com_kwh), o Analytics Comercial — a mesma linha
// (ver src/lib/metasComerciais.ts). A tela inicial (Diretoria, Gestão
// Comercial), os KPIs da aba CEO e a Performance Comercial leem daqui.
//
// Regra: a meta de um mês é a linha mais recente do indicador até aquele mês.
// Definir novembro não muda outubro; sem linha nova, dezembro repete novembro.
// Sem nenhuma linha, vale a meta do KPI validado pelo Igor e, sem ela, a
// sugerida pelo sistema (isso fica em quem lê: ver `avaliarKpi`).
//
// Função pura: roda sob `node --test` com type-stripping.

import type { KpiDepartamento, UnidadeKpi } from './kpisDepartamento.ts';
import { DEPARTAMENTOS, LIMITE_KPIS_POR_DEPARTAMENTO, kpiAtivo } from './kpisDepartamento.ts';
import { kpiAutomatico, kpisAutomaticosDe, type DefinicaoKpiAuto } from './kpisAutomaticos.ts';
import { isoDoMes, mesDoOrdinal } from './painelCeo.ts';

export interface MetaMensal {
  id: number;
  indicador: string;
  /** 1º dia do mês: 'AAAA-MM-01'. */
  mes: string;
  meta: number;
  observacao: string | null;
  atualizado_por: string | null;
  atualizado_em: string;
}

/** Indicador fixo da meta comercial do time: o kWh aprovado no mês. */
export const INDICADOR_ENERGIA_COMERCIAL = 'com_kwh';

/**
 * Indicadores que a tela inicial lê e que, por isso, aparecem sempre na aba Metas,
 * mesmo que não estejam entre os 3 KPIs do departamento: a meta comercial do time
 * (Gestão Comercial e Diretoria) e as duas metas do plano de bônus que a Gestão do
 * Financeiro acompanha.
 */
export const INDICADORES_FIXOS: Record<string, string[]> = {
  comercial: [INDICADOR_ENERGIA_COMERCIAL],
  financeiro: ['fin_inadimplencia_bonus', 'fin_prazo'],
  // Verba de mídia e orçamento de ferramentas (#6509): as telas de Marketing e do Felipe.
  marketing: ['mkt_investimento'],
  tecnologia: ['tec_custo_ferramentas'],
};

/** Chave de um KPI salvo: o id do cálculo automático ou `kpi:<id>` para um KPI próprio. */
export const chaveDoKpi = (k: Pick<KpiDepartamento, 'id' | 'calculo_automatico'>): string =>
  k.calculo_automatico || `kpi:${k.id}`;

/** 'AAAA-MM' ou 'AAAA-MM-DD' → 'AAAA-MM-01'. */
export const inicioDoMes = (mes: string): string => `${String(mes).slice(0, 7)}-01`;

/** 'AAAA-MM' a partir de uma data local. */
export const mesDaData = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const ordinal = (mes: string): number => Number(mes.slice(0, 4)) * 12 + Number(mes.slice(5, 7)) - 1;
const deOrdinal = (o: number): string => `${Math.floor(o / 12)}-${String((o % 12) + 1).padStart(2, '0')}`;

/** 'AAAA-MM' → 'OUT/2026'. */
export const rotuloDoMes = (mes: string): string => mesDoOrdinal(ordinal(mes));

/** Desloca um mês 'AAAA-MM' em `n` meses. */
export const somarMeses = (mes: string, n: number): string => deOrdinal(ordinal(mes) + n);

/** Os meses da grade: `antes` meses antes do centro, o centro e `depois` depois ('AAAA-MM'). */
export function mesesAoRedor(centro: string, antes: number, depois: number): string[] {
  const c = ordinal(centro);
  return Array.from({ length: antes + depois + 1 }, (_, i) => deOrdinal(c - antes + i));
}

export interface MetaDoMes {
  valor: number;
  /** Mês da linha que vale ('AAAA-MM-01'). */
  desde: string;
  /** true = a meta foi definida num mês anterior e está sendo repetida. */
  herdada: boolean;
  linha: MetaMensal;
}

/** A meta que vale para o indicador no mês, ou null se ninguém definiu até ele. */
export function metaDoMes(linhas: readonly MetaMensal[], indicador: string, mes: string): MetaDoMes | null {
  const alvo = inicioDoMes(mes);
  let melhor: MetaMensal | null = null;
  for (const l of linhas) {
    if (l.indicador !== indicador) continue;
    const desde = String(l.mes).slice(0, 10);
    if (desde > alvo) continue;
    if (!melhor || desde > String(melhor.mes).slice(0, 10)) melhor = l;
  }
  if (!melhor) return null;
  const valor = Number(melhor.meta);
  if (!Number.isFinite(valor)) return null;
  const desde = String(melhor.mes).slice(0, 10);
  return { valor, desde, herdada: desde !== alvo, linha: melhor };
}

/**
 * Mês a que a meta de um KPI se refere: o do período do valor ("SET/2026" no
 * faturamento do último mês fechado) ou, quando o período não é um mês ("12
 * meses"), o mês do "hoje" das contas.
 */
export function mesDaMeta(periodo: string | null | undefined, hoje: Date): string {
  const iso = periodo ? isoDoMes(periodo.slice(0, 8)) : null;
  return iso ? iso.slice(0, 7) : mesDaData(hoje);
}

/** Número digitado em pt-BR: "30.000" → 30000, "12,5" → 12,5. Vazio ou inválido → null. */
export function lerNumeroBr(texto: string): number | null {
  const limpo = texto.trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  if (!limpo) return null;
  const v = Number(limpo);
  return Number.isFinite(v) && v >= 0 ? v : null;
}

// ── Indicadores que têm meta ──

export interface IndicadorDeMeta {
  chave: string;
  departamento: string;
  nome: string;
  unidade: UnidadeKpi;
  sentido: 'maior' | 'menor';
  /**
   * validado/proposto: KPI salvo na aba CEO. sugestao: ainda não validado, sai do
   * catálogo. fixo: entra sempre, porque a tela inicial depende dele (INDICADORES_FIXOS).
   */
  situacao: 'validado' | 'proposto' | 'sugestao' | 'fixo';
  /** Meta do KPI salvo (vale nos meses sem meta definida). */
  metaBase: number | null;
  def?: DefinicaoKpiAuto;
  kpi?: KpiDepartamento;
}

export interface DepartamentoDeMeta {
  id: string;
  rotulo: string;
  indicadores: IndicadorDeMeta[];
}

/**
 * Os indicadores de cada departamento, na mesma escolha da aba CEO: os KPIs
 * salvos que não foram recusados e, até completar 3, as sugestões do catálogo.
 * Mais os indicadores que a tela inicial lê (INDICADORES_FIXOS).
 */
export function indicadoresDeMeta(salvos: readonly KpiDepartamento[]): DepartamentoDeMeta[] {
  return DEPARTAMENTOS.map(({ id, rotulo }) => {
    const doDep = salvos.filter((k) => k.departamento === id);
    const ativos = doDep.filter(kpiAtivo);
    const usados = new Set(doDep.map((k) => k.calculo_automatico).filter(Boolean));
    const vagas = Math.max(0, LIMITE_KPIS_POR_DEPARTAMENTO - ativos.length);
    const indicadores: IndicadorDeMeta[] = [
      ...ativos.map((k): IndicadorDeMeta => ({
        chave: chaveDoKpi(k),
        departamento: id,
        nome: k.nome,
        unidade: k.unidade,
        sentido: k.sentido,
        situacao: k.status === 'validado' ? 'validado' : 'proposto',
        metaBase: Number.isFinite(Number(k.meta)) ? Number(k.meta) : null,
        def: kpiAutomatico(k.calculo_automatico),
        kpi: k,
      })),
      ...kpisAutomaticosDe(id).filter((d) => !usados.has(d.id)).slice(0, vagas).map((d): IndicadorDeMeta => ({
        chave: d.id, departamento: id, nome: d.nome, unidade: d.unidade, sentido: d.sentido,
        situacao: 'sugestao', metaBase: null, def: d,
      })),
    ];
    for (const fixo of INDICADORES_FIXOS[id] ?? []) {
      const d = kpiAutomatico(fixo);
      if (!d || indicadores.some((i) => i.chave === fixo)) continue;
      indicadores.push({
        chave: d.id, departamento: id, nome: d.nome, unidade: d.unidade, sentido: d.sentido,
        situacao: 'fixo', metaBase: null, def: d,
      });
    }
    return { id, rotulo, indicadores };
  });
}
